import type { ReactElement, ReactNode } from 'react';
import './DesignSystem.css';

export type CongestionStatus = 'Comfortable' | 'Congested';

export interface KpiSummaryStripProps {
  readonly weeklyPassengers: number;
  /** Sub-text under the passenger count, e.g. "142 movements". */
  readonly movementsLabel: string;
  /** 0-100. */
  readonly loadFactorPct: number;
  readonly weeklySeats: number;
  readonly activeRoutes: number;
  readonly connectedAirports: number;
  readonly marketPopulation: number | null;
  readonly eligibleTravelers: number | null;
  readonly congestion: CongestionStatus;
  /** 0-100 slot pressure. */
  readonly pressurePct: number;
}

const INT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function Column({ label, value, sub }: { readonly label: string; readonly value: ReactNode; readonly sub: string }): ReactElement {
  return (
    <div className="fl-kpi">
      <div className="fl-kpi-label">{label}</div>
      <div className="fl-kpi-value">{value}</div>
      <div className="fl-kpi-sub">{sub}</div>
    </div>
  );
}

/** Five equal columns: weekly pax, load factor, routes, market reach and airport congestion. */
export function KpiSummaryStrip(props: KpiSummaryStripProps): ReactElement {
  const reach = props.marketPopulation;
  return (
    <section className="fl-kpi-strip" aria-label="Airport summary">
      <Column label="Weekly passengers" value={INT.format(props.weeklyPassengers)} sub={props.movementsLabel} />
      <Column
        label="Average load factor"
        value={`${props.loadFactorPct.toFixed(1)}%`}
        sub={`${INT.format(props.weeklySeats)} seats / week`}
      />
      <Column
        label="Active routes"
        value={INT.format(props.activeRoutes)}
        sub={`${INT.format(props.connectedAirports)} connected airports`}
      />
      <Column
        label="Market reach"
        value={reach === null ? '–' : INT.format(reach)}
        sub={props.eligibleTravelers === null ? 'Select to load' : `${INT.format(props.eligibleTravelers)} eligible travelers`}
      />
      <Column
        label="Airport congestion"
        value={
          <span className={`fl-pill${props.congestion === 'Congested' ? ' fl-pill-bad' : ''}`}>{props.congestion}</span>
        }
        sub={`${props.pressurePct.toFixed(0)}% pressure`}
      />
    </section>
  );
}
